import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { calculateAge } from '../utils/ageCalculator';
import AvatarDisplay from './AvatarDisplay';

interface Student {
  uid: string;
  name: string;
  nickname: string;
  instrument: string;
  birthdate?: string;
  planStatus: 'active' | 'expired';
  avatarUrl?: string;
  practiceSessionDuration?: number;
  practiceSessionsPerWeek?: number;
}

interface PracticeSession {
  id: string;
  studentId: string;
  lessonId: string;
  plannedTotalMinutes: number;
  plannedDurations: Record<string, number>;
  actualDurations: Record<string, number>;
  completed: boolean;
  startAt: any;
  endAt?: any;
  durationSec?: number;
  taskCounts: Record<string, number>;
  createdAt: any;
  sessionType?: string;
  categoryTimers?: Record<string, { isActive: boolean; elapsed: number }>;
}

interface Task {
  id?: string;
  title: string;
  category: 'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble';
  isCustom: boolean;
  createdBy?: 'student' | 'teacher';
  teacherId?: string;
  practiceReminders?: (string | { content: string; type?: string; level?: number })[];
  defaultMinutes?: number;
}

interface TipResponse {
  id: string;
  studentId: string;
  tipText: string;
  response: 'consistent' | 'improving' | 'no_improvement';
  timestamp: any;
  sessionId: string;
}

const TeacherAnalytics: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId: string }>();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Analytics data
  const [weeklyPracticeTime, setWeeklyPracticeTime] = useState(0);
  const [yearlyPracticeTime, setYearlyPracticeTime] = useState(0);
  const [dailyGoalReached, setDailyGoalReached] = useState(0);
  const [categoryBreakdown, setCategoryBreakdown] = useState<Record<string, number>>({});
  const [categoryBreakdownDetailed, setCategoryBreakdownDetailed] = useState<Record<string, { guided: number; free: number }>>({});
  const [totalSessions, setTotalSessions] = useState(0);
  const [averageSessionLength, setAverageSessionLength] = useState(0);
  const [recentSessions, setRecentSessions] = useState<PracticeSession[]>([]);
  const [isSessionsExpanded, setIsSessionsExpanded] = useState(false);
  
  // Tip response analytics
  const [tipResponses, setTipResponses] = useState<TipResponse[]>([]);
  const [tipResponseStats, setTipResponseStats] = useState<{
    consistent: number;
    improving: number;
    no_improvement: number;
    total: number;
  }>({ consistent: 0, improving: 0, no_improvement: 0, total: 0 });

  useEffect(() => {
    if (currentUser?.role !== 'teacher') {
      navigate('/dashboard');
      return;
    }
    
    if (studentId) {
      fetchStudentAndAnalytics();
    }
  }, [currentUser, navigate, studentId]);

  const fetchStudentAndAnalytics = async () => {
    if (!studentId) return;
    
    try {
      setLoading(true);
      
      // Fetch student data
      const studentDoc = await getDoc(doc(db, 'users', studentId));
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        setStudent({
          uid: studentId,
          ...studentData
        } as Student);
      } else {
        setError('Student not found');
        return;
      }
      
      // Load analytics data for the student
      const studentData = {
        uid: studentId,
        ...studentDoc.data()
      } as Student;
      await loadAnalyticsData(studentId, studentData);
      
    } catch (error) {
      console.error('Error fetching student analytics:', error);
      setError('Failed to load student analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async (targetStudentId: string, studentData?: Student) => {
    try {
      // Load all completed practice sessions for the student
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', targetStudentId),
        where('completed', '==', true)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessions: PracticeSession[] = [];
      
      sessionsSnapshot.forEach((doc) => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          studentId: data.studentId,
          lessonId: data.lessonId,
          plannedTotalMinutes: data.plannedTotalMinutes,
          plannedDurations: data.plannedDurations,
          actualDurations: data.actualDurations,
          completed: data.completed,
          startAt: data.startAt,
          endAt: data.endAt,
          durationSec: data.durationSec,
          taskCounts: data.taskCounts,
          createdAt: data.createdAt,
          sessionType: data.sessionType,
          categoryTimers: data.categoryTimers
        });
      });

      // Sort sessions by endAt date (most recent first)
      sessions.sort((a, b) => {
        const aTime = a.endAt?.toDate?.() || new Date(a.endAt || 0);
        const bTime = b.endAt?.toDate?.() || new Date(b.endAt || 0);
        return bTime.getTime() - aTime.getTime();
      });

      // Calculate weekly practice time
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay()); // Go back to Sunday
      startOfWeek.setHours(0, 0, 0, 0);
      
      const weeklySessions = sessions.filter(session => {
        const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt);
        return sessionDate >= startOfWeek;
      });
      
      const weeklyTime = weeklySessions.reduce((total, session) => {
        return total + (session.durationSec || 0);
      }, 0);
      setWeeklyPracticeTime(weeklyTime);

      // Calculate yearly practice time
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const yearlySessions = sessions.filter(session => {
        const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt);
        return sessionDate >= startOfYear;
      });
      
      const yearlyTime = yearlySessions.reduce((total, session) => {
        return total + (session.durationSec || 0);
      }, 0);
      setYearlyPracticeTime(yearlyTime);

      // Calculate daily goal reached count - only count sessions of 10+ minutes
      const goalMinutes = (studentData || student)?.practiceSessionDuration || 30;
      const goalReachedCount = sessions.filter(session => {
        const sessionDurationMinutes = (session.durationSec || 0) / 60;
        return sessionDurationMinutes >= 10 && sessionDurationMinutes >= goalMinutes;
      }).length;
      setDailyGoalReached(goalReachedCount);

      // Calculate category breakdown (separated by session type)
      const categoryBreakdown: Record<string, { guided: number; free: number }> = {
        'Scales': { guided: 0, free: 0 },
        'Exercises': { guided: 0, free: 0 },
        'Studies': { guided: 0, free: 0 },
        'Repertoire': { guided: 0, free: 0 },
        'Ensemble': { guided: 0, free: 0 }
      };

      // Load custom tasks to get category information
      const customTasksQuery = query(
        collection(db, 'customTasks'),
        where('studentId', '==', targetStudentId)
      );
      const customTasksSnapshot = await getDocs(customTasksQuery);
      const customTasks: Task[] = [];
      
      customTasksSnapshot.forEach((doc) => {
        const data = doc.data();
        customTasks.push({
          id: doc.id,
          title: data.title,
          category: data.category,
          isCustom: data.isCustom,
          createdBy: data.createdBy,
          teacherId: data.teacherId,
          practiceReminders: data.practiceReminders,
          defaultMinutes: data.defaultMinutes
        });
      });

      // Calculate time spent in each category
      sessions.forEach(session => {
        // Handle Free Practice sessions
        if (session.sessionType === 'free_practice' && session.categoryTimers) {
          Object.entries(session.categoryTimers).forEach(([category, timerData]) => {
            if (timerData.elapsed > 0) {
              const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
              const minutes = Math.round((timerData.elapsed / 60) * 100) / 100;
              if (categoryBreakdown.hasOwnProperty(categoryName)) {
                categoryBreakdown[categoryName].free += minutes;
              }
            }
          });
        } else {
          // Handle regular practice sessions (Guided Practice)
          Object.entries(session.actualDurations).forEach(([taskId, minutes]) => {
            // Determine category based on taskId
            let category = 'Repertoire'; // Default category
            
            if (taskId.startsWith('free_')) {
              // Handle free practice category timers (shouldn't happen in guided sessions)
              const categoryName = taskId.replace('free_', '');
              category = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
            } else if (taskId.startsWith('custom_')) {
              const customTaskId = taskId.replace('custom_', '');
              const customTask = customTasks.find(task => task.id === customTaskId);
              if (customTask) {
                category = customTask.category;
              }
            } else if (taskId.startsWith('lesson_')) {
              // For lesson tasks, we'll need to get the category from the lesson
              // For now, we'll use a simple mapping based on common patterns
              category = 'Repertoire'; // Default for lesson tasks
            }
            
            if (categoryBreakdown.hasOwnProperty(category)) {
              categoryBreakdown[category].guided += minutes;
            }
          });
        }
      });

      // Convert to the old format for compatibility
      const categoryTime: Record<string, number> = {};
      Object.keys(categoryBreakdown).forEach(category => {
        categoryTime[category] = categoryBreakdown[category].guided + categoryBreakdown[category].free;
      });

      setCategoryBreakdown(categoryTime);
      setCategoryBreakdownDetailed(categoryBreakdown);

      // Calculate additional stats - only count sessions of 10+ minutes
      const validSessions = sessions.filter(session => {
        const sessionDurationMinutes = (session.durationSec || 0) / 60;
        return sessionDurationMinutes >= 10;
      });
      setTotalSessions(validSessions.length);
      
      if (validSessions.length > 0) {
        const totalTime = validSessions.reduce((total, session) => total + (session.durationSec || 0), 0);
        const averageMinutes = totalTime / 60 / validSessions.length;
        setAverageSessionLength(averageMinutes);
      }

      // Store the 14 most recent sessions for the expandable card
      setRecentSessions(sessions.slice(0, 14));

      // Load tip responses
      await loadTipResponses(targetStudentId);

    } catch (error) {
      console.error('Error loading analytics data:', error);
      setError('Failed to load analytics data');
    }
  };

  const loadTipResponses = async (targetStudentId: string) => {
    try {
      const tipResponsesQuery = query(
        collection(db, 'tipResponses'),
        where('studentId', '==', targetStudentId)
      );
      
      const tipResponsesSnapshot = await getDocs(tipResponsesQuery);
      const responses: TipResponse[] = [];
      
      tipResponsesSnapshot.forEach((doc) => {
        const data = doc.data();
        responses.push({
          id: doc.id,
          studentId: data.studentId,
          tipText: data.tipText,
          response: data.response,
          timestamp: data.timestamp,
          sessionId: data.sessionId
        });
      });
      
      setTipResponses(responses);
      
      // Calculate statistics
      const stats = {
        consistent: responses.filter(r => r.response === 'consistent').length,
        improving: responses.filter(r => r.response === 'improving').length,
        no_improvement: responses.filter(r => r.response === 'no_improvement').length,
        total: responses.length
      };
      
      setTipResponseStats(stats);
      
    } catch (error) {
      console.error('Error loading tip responses:', error);
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    } else {
      return `${mins}m`;
    }
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      'Scales': 'bg-gradient-to-r from-blue-500 to-cyan-500',
      'Exercises': 'bg-gradient-to-r from-green-500 to-emerald-500',
      'Studies': 'bg-gradient-to-r from-yellow-500 to-orange-500',
      'Repertoire': 'bg-gradient-to-r from-purple-500 to-pink-500',
      'Ensemble': 'bg-gradient-to-r from-pink-500 to-rose-500'
    };
    return colors[category] || 'bg-gradient-to-r from-gray-500 to-gray-600';
  };

  const getCategoryIcon = (category: string): string => {
    const icons: Record<string, string> = {
      'Scales': '🎼',
      'Exercises': '💪',
      'Studies': '📚',
      'Repertoire': '🎵',
      'Ensemble': '👥'
    };
    return icons[category] || '🎶';
  };

  const formatSessionDate = (session: PracticeSession): string => {
    const endDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
    return endDate.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatSessionTime = (session: PracticeSession): string => {
    const startTime = session.startAt?.toDate?.() || new Date(session.startAt || 0);
    return startTime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatSessionDuration = (session: PracticeSession): string => {
    const durationMinutes = (session.durationSec || 0) / 60;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = Math.floor(durationMinutes % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getSessionStatusColor = (session: PracticeSession): string => {
    const durationMinutes = (session.durationSec || 0) / 60;
    const goalMinutes = student?.practiceSessionDuration || 30;
    
    if (durationMinutes >= goalMinutes) {
      return 'text-green-600 bg-green-50 border-green-200';
    } else if (durationMinutes >= goalMinutes * 0.5) {
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    } else {
      return 'text-red-600 bg-red-50 border-red-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading student analytics...</p>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Student Not Found</h2>
          <p className="text-gray-600 mb-4">The student you're looking for doesn't exist or you don't have access.</p>
          <button
            onClick={() => navigate('/teacher/dashboard')}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-4xl mr-3">📊</div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Student Analytics</h1>
                  <p className="text-white/90 mt-1">
                    {student.name} ({student.nickname}) - {student.instrument}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/teacher/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Back to Dashboard
              </button>
              <button
                onClick={logout}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Student Info Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl mb-8 border border-gray-100">
            <div className="px-6 py-8 sm:p-8">
              <div className="flex items-center space-x-4">
                <AvatarDisplay 
                  user={student}
                  size="lg"
                  alt={`${student.name}'s avatar`}
                />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{student.name} ({student.nickname})</h2>
                  <p className="text-gray-600">{student.instrument} • Age {student.birthdate ? calculateAge(student.birthdate) : 'Not set'}</p>
                  <div className="flex items-center mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      student.planStatus === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {student.planStatus}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      Daily goal: {student.practiceSessionDuration || 30} min • Weekly target: {student.practiceSessionsPerWeek || 5} sessions
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 overflow-hidden shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-3xl animate-pulse">📅</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-blue-100 truncate">This Week</dt>
                      <dd className="text-2xl font-bold text-white">{formatTime(weeklyPracticeTime)}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-emerald-500 overflow-hidden shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-3xl animate-pulse">📆</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-green-100 truncate">This Year</dt>
                      <dd className="text-2xl font-bold text-white">{formatTime(yearlyPracticeTime)}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-red-500 overflow-hidden shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-3xl animate-bounce">🎯</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-orange-100 truncate">Goals Reached</dt>
                      <dd className="text-2xl font-bold text-white">{dailyGoalReached}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-3xl animate-pulse">📊</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-purple-100 truncate">Total Sessions</dt>
                      <dd className="text-2xl font-bold text-white">{totalSessions}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl mb-8 border border-gray-100">
            <div className="px-6 py-8 sm:p-8">
              <div className="flex items-center mb-6">
                <div className="text-3xl mr-3">🎨</div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Practice Time by Category</h3>
              </div>
              
              <div className="space-y-6">
                {['Scales', 'Exercises', 'Studies', 'Repertoire', 'Ensemble'].map((category, index) => {
                  const minutes = categoryBreakdown[category] || 0;
                  const detailed = categoryBreakdownDetailed[category] || { guided: 0, free: 0 };
                  const totalMinutes = Object.values(categoryBreakdown).reduce((sum, min) => sum + min, 0);
                  const percentage = totalMinutes > 0 ? (minutes / totalMinutes) * 100 : 0;
                  const hasPractice = minutes > 0;
                  
                  return (
                    <div key={category} className={`group ${!hasPractice ? 'opacity-60' : ''}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div className={`text-3xl mr-3 group-hover:scale-110 transition-transform duration-200 ${!hasPractice ? 'grayscale' : ''}`}>
                            {getCategoryIcon(category)}
                          </div>
                          <span className={`text-lg font-semibold ${hasPractice ? 'text-gray-800' : 'text-gray-500'}`}>
                            {category}
                            {!hasPractice && <span className="text-sm ml-2 text-gray-400">(No practice yet)</span>}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className={`text-xl font-bold ${hasPractice ? 'text-gray-900' : 'text-gray-400'}`}>
                            {formatMinutes(minutes)}
                          </span>
                          {hasPractice && (
                            <span className="text-sm text-gray-500 ml-2">({percentage.toFixed(1)}%)</span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {/* Guided Practice Bar */}
                        <div className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-600">Guided Practice</span>
                            <span className="text-sm text-gray-500">{formatMinutes(detailed.guided)}</span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-4 shadow-inner">
                            {detailed.guided > 0 && (
                              <div
                                className={`${getCategoryColor(category)} h-4 rounded-full transition-all duration-1000 ease-out shadow-md group-hover:shadow-lg ${!hasPractice ? 'opacity-30' : ''}`}
                                style={{ 
                                  width: `${Math.max((detailed.guided / totalMinutes) * 100, 2)}%`,
                                  animationDelay: `${index * 200}ms`
                                }}
                              ></div>
                            )}
                          </div>
                        </div>
                        
                        {/* Free Practice Bar */}
                        <div className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-600">Free Practice</span>
                            <span className="text-sm text-gray-500">{formatMinutes(detailed.free)}</span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-4 shadow-inner">
                            {detailed.free > 0 && (
                              <div
                                className={`${getCategoryColor(category)} h-4 rounded-full transition-all duration-1000 ease-out shadow-md group-hover:shadow-lg ${!hasPractice ? 'opacity-30' : ''}`}
                                style={{ 
                                  width: `${Math.max((detailed.free / totalMinutes) * 100, 2)}%`,
                                  animationDelay: `${index * 200 + 100}ms`,
                                  opacity: 0.8
                                }}
                              ></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent Practice Sessions - Expandable Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl mb-8 border border-gray-100">
            <div className="px-6 py-8 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="text-3xl mr-3">📋</div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Recent Practice Sessions
                  </h3>
                  <span className="ml-3 text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    Last 14 sessions
                  </span>
                </div>
                <button
                  onClick={() => setIsSessionsExpanded(!isSessionsExpanded)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <span className="font-medium">
                    {isSessionsExpanded ? 'Collapse' : 'Expand'}
                  </span>
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${isSessionsExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {recentSessions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🎵</div>
                  <h4 className="text-xl font-semibold text-gray-700 mb-2">No Practice Sessions Yet</h4>
                  <p className="text-gray-500">This student hasn't completed any practice sessions yet.</p>
                </div>
              ) : (
                <div className={`transition-all duration-300 overflow-hidden ${
                  isSessionsExpanded ? 'max-h-none' : 'max-h-0'
                }`}>
                  <div className="space-y-3">
                    {recentSessions.map((session, index) => (
                      <div 
                        key={session.id} 
                        className={`bg-white rounded-lg border-2 p-4 shadow-sm hover:shadow-md transition-all duration-200 ${
                          getSessionStatusColor(session)
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {index + 1}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-4">
                                <div>
                                  <p className="text-lg font-semibold text-gray-900">
                                    {formatSessionDate(session)}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    Started at {formatSessionTime(session)}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xl font-bold text-gray-900">
                                    {formatSessionDuration(session)}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    Duration
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              (session.durationSec || 0) / 60 >= (student?.practiceSessionDuration || 30)
                                ? 'bg-green-100 text-green-800'
                                : (session.durationSec || 0) / 60 >= (student?.practiceSessionDuration || 30) * 0.5
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {(session.durationSec || 0) / 60 >= (student?.practiceSessionDuration || 30)
                                ? 'Goal Met ✓'
                                : (session.durationSec || 0) / 60 >= (student?.practiceSessionDuration || 30) * 0.5
                                ? 'Partial Goal'
                                : 'Below Goal'
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary when collapsed */}
              {!isSessionsExpanded && recentSessions.length > 0 && (
                <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-6">
                      <div>
                        <span className="text-gray-600">Most Recent:</span>
                        <span className="ml-2 font-semibold text-gray-900">
                          {formatSessionDate(recentSessions[0])} at {formatSessionTime(recentSessions[0])}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Duration:</span>
                        <span className="ml-2 font-semibold text-gray-900">
                          {formatSessionDuration(recentSessions[0])}
                        </span>
                      </div>
                    </div>
                    <div className="text-indigo-600 font-medium">
                      Click expand to view all {recentSessions.length} sessions
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="px-6 py-8 sm:p-8">
                <div className="flex items-center mb-6">
                  <div className="text-3xl mr-3">📈</div>
                  <h3 className="text-2xl font-bold text-white">Session Statistics</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-indigo-100 font-medium">Average Session Length</span>
                    <span className="text-xl font-bold text-white">{formatMinutes(averageSessionLength)}</span>
                  </div>
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-indigo-100 font-medium">Total Practice Time</span>
                    <span className="text-xl font-bold text-white">{formatTime(yearlyPracticeTime)}</span>
                  </div>
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-indigo-100 font-medium">Daily Goal</span>
                    <span className="text-xl font-bold text-white">{student.practiceSessionDuration || 30} minutes</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-pink-500 to-rose-600 shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="px-6 py-8 sm:p-8">
                <div className="flex items-center mb-6">
                  <div className="text-3xl mr-3">💡</div>
                  <h3 className="text-2xl font-bold text-white">Progress Insights</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-pink-100 font-medium">Goal Success Rate</span>
                    <span className="text-xl font-bold text-white">
                      {totalSessions > 0 ? ((dailyGoalReached / totalSessions) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-pink-100 font-medium">Most Practiced Category</span>
                    <span className="text-xl font-bold text-white">
                      {Object.keys(categoryBreakdown).length > 0 
                        ? Object.entries(categoryBreakdown).reduce((a, b) => categoryBreakdown[a[0]] > categoryBreakdown[b[0]] ? a : b)[0]
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-pink-100 font-medium">Weekly Target</span>
                    <span className="text-xl font-bold text-white">
                      {student.practiceSessionsPerWeek || 5} sessions
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tip Response Analytics */}
          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl mb-8 border border-gray-100">
            <div className="px-6 py-8 sm:p-8">
              <div className="flex items-center mb-6">
                <div className="text-3xl mr-3">💡</div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Tip Practice Feedback
                </h3>
                <span className="ml-3 text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {tipResponseStats.total} responses
                </span>
              </div>
              
              {tipResponseStats.total > 0 ? (
                <div className="space-y-6">
                  {/* Response Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">Consistently Applied</p>
                          <p className="text-2xl font-bold text-green-600">{tipResponseStats.consistent}</p>
                        </div>
                        <div className="text-2xl">✓</div>
                      </div>
                      <div className="mt-2">
                        <div className="bg-green-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-1000"
                            style={{ width: `${tipResponseStats.total > 0 ? (tipResponseStats.consistent / tipResponseStats.total) * 100 : 0}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-green-600 mt-1">
                          {tipResponseStats.total > 0 ? Math.round((tipResponseStats.consistent / tipResponseStats.total) * 100) : 0}%
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-yellow-800">Getting Better</p>
                          <p className="text-2xl font-bold text-yellow-600">{tipResponseStats.improving}</p>
                        </div>
                        <div className="text-2xl">📈</div>
                      </div>
                      <div className="mt-2">
                        <div className="bg-yellow-200 rounded-full h-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full transition-all duration-1000"
                            style={{ width: `${tipResponseStats.total > 0 ? (tipResponseStats.improving / tipResponseStats.total) * 100 : 0}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-yellow-600 mt-1">
                          {tipResponseStats.total > 0 ? Math.round((tipResponseStats.improving / tipResponseStats.total) * 100) : 0}%
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-red-800">Needs More Practice</p>
                          <p className="text-2xl font-bold text-red-600">{tipResponseStats.no_improvement}</p>
                        </div>
                        <div className="text-2xl">❌</div>
                      </div>
                      <div className="mt-2">
                        <div className="bg-red-200 rounded-full h-2">
                          <div 
                            className="bg-red-500 h-2 rounded-full transition-all duration-1000"
                            style={{ width: `${tipResponseStats.total > 0 ? (tipResponseStats.no_improvement / tipResponseStats.total) * 100 : 0}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-red-600 mt-1">
                          {tipResponseStats.total > 0 ? Math.round((tipResponseStats.no_improvement / tipResponseStats.total) * 100) : 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Recent Tip Responses */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Recent Tip Feedback</h4>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {tipResponses.slice(0, 10).map((response) => (
                        <div key={response.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className={`w-3 h-3 rounded-full mt-1 ${
                            response.response === 'consistent' ? 'bg-green-500' :
                            response.response === 'improving' ? 'bg-yellow-500' : 'bg-red-500'
                          }`}></div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-800 font-medium">{response.tipText}</p>
                            <div className="flex items-center justify-between mt-1">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                response.response === 'consistent' ? 'bg-green-100 text-green-800' :
                                response.response === 'improving' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {response.response === 'consistent' ? '✓ Consistently Applied' :
                                 response.response === 'improving' ? '📈 Getting Better' : '❌ Needs More Practice'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {response.timestamp?.toDate?.() ? 
                                  response.timestamp.toDate().toLocaleDateString() : 
                                  'Unknown date'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">💡</div>
                  <p className="text-gray-500 text-lg">No tip feedback yet</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Tip feedback will appear here when students complete practice tips
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TeacherAnalytics;
